import React, { forwardRef, useId } from "react";
import classNames from "classnames";
import { Checkbox as OmegaCheckbox } from "@omega/ui";
import "./Checkbox.scss";

/** Размер чекбокса. */
export type CheckboxSize = "sm" | "md";

/** Пропсы {@link Checkbox}. */
export interface CheckboxProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type" | "checked" | "defaultChecked"> {
    /** Подпись справа от чекбокса. */
    label?: React.ReactNode;
    /** Подсказка под подписью. */
    description?: React.ReactNode;
    /** Контролируемое состояние отмечен/не отмечен. */
    checked?: boolean;
    /** Mixed-state для сценариев частичного выбора. */
    indeterminate?: boolean;
    /** Размер квадрата чекбокса. @default "md" */
    size?: CheckboxSize;
    /** Флаг ошибки валидации. */
    error?: boolean;
}

/**
 * Shared UI чекбокс: facade над `@omega/ui` Checkbox.
 * Legacy `error` → package `invalid`; `description` остаётся app-level обёрткой.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
    {
        label,
        description,
        className,
        checked = false,
        indeterminate = false,
        disabled,
        size = "md",
        error = false,
        id: idProp,
        ...inputProps
    },
    ref,
) {
    const generatedId = useId();
    const inputId = idProp ?? generatedId;
    const descriptionId = description != null ? `${inputId}-desc` : undefined;

    const checkbox = (
        <OmegaCheckbox
            {...inputProps}
            ref={ref}
            id={inputId}
            checked={checked}
            indeterminate={indeterminate}
            disabled={disabled}
            size={size}
            invalid={error}
            label={label}
            aria-describedby={descriptionId}
            className={description == null ? className : undefined}
        />
    );

    if (description == null) {
        return checkbox;
    }

    return (
        <div
            className={classNames("shared_checkbox_facade", className, {
                shared_checkbox_facade__disabled: disabled,
                shared_checkbox_facade__error: error,
            })}
        >
            {checkbox}
            <p id={descriptionId} className="shared_checkbox_facade__description">
                {description}
            </p>
        </div>
    );
});
